import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { InfoCard } from "../../../components/cards/infoCard";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { CalendarOptionsModal } from "../../../components/modal/app_modals/calendarOptionsModal";
import { ScrollToTop } from "../../../components/scrollToTop";
import { TicketBody } from "../../../components/ticket";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { useWebsiteButton } from "../../../customeHooks/useWebsiteButtons";
import { EventGuest } from "../../../redux/features/event/event.types";
import { useAppSelector } from "../../../redux/hooks";
import {
  APP_PORT,
  APP_SCHEME,
  APP_SUBDOMAIN,
  currencyShorts,
} from "../../../utils/constants";
import {
  formatDateTime,
  formatToCurrency,
  handleDownloadPdf,
} from "../../../utils/helpers";
import { TicketPurchase } from "../../../utils/types";

export const PurchaseSuccess = ({
  guest,
  ticketsPurchase,
  isPurchasingOnlyFree = false,
}: {
  guest: EventGuest;
  ticketsPurchase: TicketPurchase[];
  isPurchasingOnlyFree?: boolean;
}) => {
  const [showTicket, setShowTicket] = useState(false);

  const { isSignedIn } = useAppSelector((state) => state.auth);

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const { goToSignup } = useWebsiteButton();

  const ticketRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();

  return (
    <div className="flex w-full justify-center">
      <ScrollToTop changeKey={pathname} rerunOnKeyChange scrollIntoView />

      <div className="mx-auto flex w-full flex-col items-center gap-3">
        {showTicket ? (
          <>
            <div className="w-full max-w-[460px]">
              <BackButton
                text="Back"
                onClick={() => {
                  if (isSignedIn) {
                    window.open(
                      `${APP_SCHEME}//${APP_SUBDOMAIN}:${APP_PORT}/home`,
                      "_self",
                    );
                  } else {
                    setShowTicket(false);
                  }
                }}
              />
            </div>

            <TicketBody
              ref={ticketRef}
              downloadView={false}
              eventName={guest?.eventId?.name || ""}
              startDate={guest?.eventId?.startDate || ""}
              endDate={guest?.eventId?.endDate || ""}
              startTime={guest?.eventId?.startTime || ""}
              location={guest?.eventId?.location || ""}
              qrCode={guest?.qrCode || ""}
              firstName={guest?.firstName || ""}
              lastName={guest?.lastName || ""}
              email={guest?.email || ""}
              ticketsPurchase={ticketsPurchase || []}
              payment={guest?.payment}
            />

            <Button
              type="button"
              className="!max-w-[460px] !rounded-2xl"
              onClick={async () => {
                await handleDownloadPdf(
                  ticketRef,
                  `${guest?.eventId?.name}_ticket`,
                );
              }}
              text="Download"
              icon="download"
              variation="white"
            />
          </>
        ) : (
          <>
            <BgWrapper className="flex w-full max-w-[460px] flex-col items-center justify-center gap-4 !py-10">
              <img
                alt="calendar"
                src="/assets/images/check-glass.png"
                className="h-[120px] w-[120px]"
              />

              <div className="mx-auto w-full max-w-[320px] text-center">
                <p className="text-32 font-fraunces font-medium text-primary">
                  {isPurchasingOnlyFree
                    ? "You’re all set! 🎉"
                    : "Your ticket purchase was successful!"}
                </p>
                <p className="mt-2 text-grey-600">
                  {isPurchasingOnlyFree
                    ? `
                  Your ticket is confirmed. We’ve also sent a copy of it to ${guest?.email}.`
                    : `Your ticket has been sent to ${guest?.email}. If you didn't get it, please contact us on Instagram or send an email to hello@usetikket.com.`}
                </p>
              </div>
            </BgWrapper>
            <BgWrapper className="max-w-[460px] !p-4 text-grey-900">
              <div className="flex items-center justify-between gap-4 border-b border-grey-100 pb-4">
                <div className="flex flex-col">
                  <p className="text-base font-bold">
                    {guest?.firstName} {guest?.lastName}
                  </p>
                  <p className="text-sm">{guest?.email}</p>
                </div>

                <ProfileIcon dimension={40} className="!p-[10px]" />
              </div>

              {ticketsPurchase.map((tp, idx) => {
                return (
                  <div
                    key={`${idx}-${tp.ticket._id}`}
                    className="flex flex-col gap-[2px] border-b border-grey-100 py-4 text-sm last:border-b-0 last:pb-0"
                  >
                    <p>
                      {tp.ticket.name} x {tp.quantity}
                    </p>
                    <p className="font-bold">
                      {tp.ticket.ticketType === "free"
                        ? "FREE"
                        : formatToCurrency(tp.quantity * tp.ticket.price, {
                            currency:
                              currencyShorts[tp.ticket.currency ?? "NGN"],
                          })}
                    </p>
                  </div>
                );
              })}
            </BgWrapper>
            <div className="grid w-full max-w-[460px] grid-cols-1 gap-3 md:grid-cols-2">
              <Button
                type="button"
                className="!rounded-2xl"
                onClick={() => {
                  handleModalOpen("calendarOptionsModal");
                }}
                text="Add to calendar"
                icon="calendar-plus"
                variation="white"
              />

              <Button
                type="button"
                className="!rounded-2xl"
                onClick={() => {
                  setShowTicket(true);
                }}
                text="View Ticket"
                icon="ticket"
                variation="white"
              />
            </div>
            {!isSignedIn && (
              <InfoCard
                title="Buy Tickets Faster ⚡ "
                text="Sign up to save your details to buy tickets in one-click."
                className="max-w-[460px] !bg-light-blue"
                rightElement={
                  <Button
                    type="button"
                    text="Sign Up"
                    onClick={() => {
                      goToSignup(
                        `&email=${encodeURIComponent(guest?.email)}&firstName=${encodeURIComponent(guest?.firstName)}&lastName=${encodeURIComponent(guest?.lastName)}`,
                      );
                    }}
                    className="!max-w-fit"
                  />
                }
              />
            )}{" "}
          </>
        )}
      </div>

      <CalendarOptionsModal
        open={modalOpen && modal === "calendarOptionsModal"}
        handleClose={() => {
          handleModalClose();
        }}
        event={{
          title: guest?.eventId?.name ?? "",
          description: guest?.eventId?.description ?? "",
          location: guest?.eventId?.location ?? "",
          start: formatDateTime(
            guest?.eventId?.startDate ?? "",
            guest?.eventId?.startTime ?? "",
            undefined,
            "h:mm A",
          ),
          end: formatDateTime(
            guest?.eventId?.endDate ?? "",
            guest?.eventId?.endTime ?? "",
            undefined,
            "h:mm A",
          ),
        }}
      />
    </div>
  );
};
